/**
 * DebtSnowball.jsx
 * 
 * A comprehensive debt snowball calculator component for mobile devices.
 * Integrates with existing bills system and provides detailed payoff schedules.
 * 
 * Features:
 * - Import bills as debts
 * - Add debt details (balance, APR, minimum payment)
 * - Calculate debt snowball schedule (smallest balance first)
 * - Track payments and mark as paid
 * - Visual effects for paid-off debts
 * - Due date alerts
 * - Mobile-optimized design
 */

import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import '../styles/DebtSnowball.css';

const DebtSnowball = ({ workspaceId, onClose }) => {
  // Simple state management (no complex hooks)
  const [bills, setBills] = useState([]);
  const [debts, setDebts] = useState([]);
  const [debtPayments, setDebtPayments] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Local states
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showAddDebtDialog, setShowAddDebtDialog] = useState(false);
  const [showEditDebtDialog, setShowEditDebtDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [selectedBills, setSelectedBills] = useState([]);
  const [editingDebt, setEditingDebt] = useState(null);
  const [extraPayment, setExtraPayment] = useState(0);
  const [payoffSchedule, setPayoffSchedule] = useState([]);
  const [scheduleViewMode, setScheduleViewMode] = useState('timeline'); // timeline, list, chart, progress
  const [newDebt, setNewDebt] = useState({
    name: '',
    currentBalance: '',
    apr: '',
    minimumPayment: '',
    dueDay: 1,
    frequency: 'Monthly'
  });

  // Simple Firebase operations
  const loadData = async () => {
    if (!workspaceId) return;
    
    setLoading(true);
    try {
      const [billsDoc, debtsDoc, paymentsDoc] = await Promise.all([
        getDoc(doc(db, `workspaces/${workspaceId}/data/bills`)),
        getDoc(doc(db, `workspaces/${workspaceId}/data/debts`)),
        getDoc(doc(db, `workspaces/${workspaceId}/data/debtPayments`))
      ]);
      
      setBills(billsDoc.exists() ? billsDoc.data().bills || [] : []);
      setDebts(debtsDoc.exists() ? debtsDoc.data().debts || [] : []);
      setDebtPayments(paymentsDoc.exists() ? paymentsDoc.data().debtPayments || {} : {});
    } catch (error) {
      console.error('❌ Error loading debt data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateDebts = async (newDebts) => {
    try {
      await setDoc(doc(db, `workspaces/${workspaceId}/data/debts`), {
        debts: newDebts,
        lastUpdated: new Date()
      });
      setDebts(newDebts);
    } catch (error) {
      console.error('❌ Error updating debts:', error);
    }
  };

  const updateDebtPayments = async (newPayments) => {
    try {
      await setDoc(doc(db, `workspaces/${workspaceId}/data/debtPayments`), {
        debtPayments: newPayments,
        lastUpdated: new Date()
      });
      setDebtPayments(newPayments);
    } catch (error) {
      console.error('❌ Error updating debt payments:', error);
    }
  };

  // Load data when workspace changes
  useEffect(() => {
    if (workspaceId) {
      loadData();
    }
  }, [workspaceId]);

  // Calculate debt snowball schedule
  const calculateSnowballSchedule = () => {
    if (!debts.length) return [];

    // Sort debts by current balance (smallest first)
    const sortedDebts = [...debts].sort((a, b) => a.currentBalance - b.currentBalance);
    const schedule = [];
    const debtsCopy = sortedDebts.map(debt => ({ ...debt }));
    
    let currentDate = new Date();
    let month = 0;
    let availableExtra = parseFloat(extraPayment) || 0;

    while (debtsCopy.some(debt => debt.currentBalance > 0)) {
      month++;
      const monthData = {
        month,
        date: new Date(currentDate.getFullYear(), currentDate.getMonth() + month, 1),
        debts: [],
        totalPaid: 0,
        remainingBalance: 0
      };

      // Find the first debt that's not paid off
      const activeDebtIndex = debtsCopy.findIndex(debt => debt.currentBalance > 0);
      
      debtsCopy.forEach((debt, index) => {
        if (debt.currentBalance <= 0) {
          monthData.debts.push({
            ...debt,
            payment: 0,
            remainingBalance: 0,
            isPaidOff: true
          });
          return;
        }

        const monthlyRate = (debt.apr / 100) / 12;
        const interestCharge = debt.currentBalance * monthlyRate;
        let payment = debt.minimumPayment;

        // Add extra payment to the smallest debt
        if (index === activeDebtIndex) {
          payment += availableExtra;
        }

        // Don't pay more than the remaining balance + interest
        payment = Math.min(payment, debt.currentBalance + interestCharge);
        
        const principalPayment = payment - interestCharge;
        debt.currentBalance = Math.max(0, debt.currentBalance - principalPayment);

        const isPaidOff = debt.currentBalance === 0;
        
        // If this debt is paid off, add its minimum payment to extra payment pool
        if (isPaidOff && index === activeDebtIndex) {
          availableExtra += debt.minimumPayment;
        }

        monthData.debts.push({
          ...debt,
          payment,
          interestCharge,
          principalPayment,
          remainingBalance: debt.currentBalance,
          isPaidOff
        });

        monthData.totalPaid += payment;
        monthData.remainingBalance += debt.currentBalance;
      });

      schedule.push(monthData);

      // Safety check to prevent infinite loops
      if (month > 600) break; // 50 years max
    }

    return schedule;
  };

  // Update schedule when debts or extra payment changes
  useEffect(() => {
    if (debts.length > 0) {
      const schedule = calculateSnowballSchedule();
      setPayoffSchedule(schedule);
    }
  }, [debts, extraPayment]);

  // Handle importing bills as debts
  const handleImportBills = async () => {
    const newDebts = selectedBills.map(billId => {
      const bill = bills.find(b => b.id === billId);
      return {
        id: Date.now() + Math.random(),
        name: bill.name,
        currentBalance: bill.amount * 10, // Default to 10x monthly payment as balance
        apr: 18.99, // Default APR
        minimumPayment: bill.amount,
        dueDay: bill.dueDay,
        frequency: bill.frequency,
        originalBillId: bill.id,
        createdAt: new Date().toISOString()
      };
    });

    const updatedDebts = [...debts, ...newDebts];
    await updateDebts(updatedDebts);
    setSelectedBills([]);
    setShowImportDialog(false);
  };

  // Handle adding manual debt
  const handleAddDebt = async () => {
    if (!newDebt.name || !newDebt.currentBalance || !newDebt.minimumPayment) return;

    const debt = {
      id: Date.now() + Math.random(),
      name: newDebt.name,
      currentBalance: parseFloat(newDebt.currentBalance),
      apr: parseFloat(newDebt.apr) || 0,
      minimumPayment: parseFloat(newDebt.minimumPayment),
      dueDay: parseInt(newDebt.dueDay),
      frequency: newDebt.frequency,
      createdAt: new Date().toISOString()
    };

    const updatedDebts = [...debts, debt];
    await updateDebts(updatedDebts);
    setNewDebt({
      name: '',
      currentBalance: '',
      apr: '',
      minimumPayment: '',
      dueDay: 1,
      frequency: 'Monthly'
    });
    setShowAddDebtDialog(false);
  };

  // Handle editing debt
  const handleEditDebt = async () => {
    if (!editingDebt) return;

    const updatedDebts = debts.map(debt => 
      debt.id === editingDebt.id ? editingDebt : debt
    );
    await updateDebts(updatedDebts);
    setEditingDebt(null);
    setShowEditDebtDialog(false);
  };

  // Handle removing debt
  const handleRemoveDebt = async (debtId) => {
    const updatedDebts = debts.filter(debt => debt.id !== debtId);
    await updateDebts(updatedDebts);
  };

  // Handle marking payment as paid
  const handleMarkPaymentPaid = async (debtId, month, payment) => {
    const paymentKey = `${debtId}-${month}`;
    const updatedPayments = {
      ...debtPayments,
      [paymentKey]: {
        paidAt: new Date().toISOString(),
        amount: payment
      }
    };
    await updateDebtPayments(updatedPayments);

    // Update debt balance
    const updatedDebts = debts.map(debt => {
      if (debt.id === debtId) {
        const scheduleItem = payoffSchedule.find(s => s.month === month);
        const debtInSchedule = scheduleItem?.debts.find(d => d.id === debtId);
        if (debtInSchedule) {
          return {
            ...debt,
            currentBalance: Math.max(0, debt.currentBalance - debtInSchedule.principalPayment)
          };
        }
      }
      return debt;
    });
    await updateDebts(updatedDebts);
  };

  // Check if payment is paid
  const isPaymentPaid = (debtId, month) => {
    const paymentKey = `${debtId}-${month}`;
    return !!debtPayments[paymentKey];
  };

  // Check if debt is completely paid off
  const isDebtPaidOff = (debtId) => {
    const debt = debts.find(d => d.id === debtId);
    return debt && debt.currentBalance <= 0;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  // Get total debt
  const getTotalDebt = () => {
    return debts.reduce((sum, debt) => sum + debt.currentBalance, 0);
  };

  // Get total minimum payments
  const getTotalMinimumPayments = () => {
    return debts.reduce((sum, debt) => sum + debt.minimumPayment, 0);
  };

  // Get payoff date for specific debt
  const getDebtPayoffDate = (debtId) => {
    const payoffMonth = payoffSchedule.find(month => 
      month.debts.find(d => d.id === debtId && d.isPaidOff)
    );
    return payoffMonth ? payoffMonth.date : null;
  };

  // Get debt-free date
  const getDebtFreeDate = () => {
    if (payoffSchedule.length === 0) return null;
    const lastMonth = payoffSchedule[payoffSchedule.length - 1];
    return lastMonth.date;
  };

  // Calculate progress data for visualization
  const getProgressData = () => {
    const progressData = debts.map(debt => {
      const totalMonths = payoffSchedule.length;
      const payoffMonth = payoffSchedule.findIndex(month => 
        month.debts.find(d => d.id === debt.id && d.isPaidOff)
      ) + 1;
      
      return {
        ...debt,
        payoffMonth: payoffMonth || totalMonths,
        progressPercentage: payoffMonth ? (payoffMonth / totalMonths) * 100 : 100
      };
    });
    
    return progressData.sort((a, b) => a.payoffMonth - b.payoffMonth);
  };

  // Get monthly totals for chart
  const getChartData = () => {
    return payoffSchedule.slice(0, 36).map(month => ({
      month: month.month,
      date: month.date,
      totalDebt: month.remainingBalance,
      totalPayment: month.totalPaid,
      debtsRemaining: month.debts.filter(d => d.remainingBalance > 0).length
    }));
  };

  // Timeline View Component
  const TimelineView = () => {
    const progressData = getProgressData();
    const totalMonths = payoffSchedule.length;
    
    return (
      <div className="timeline-view">
        <div className="timeline-header">
          <div className="timeline-start">Start</div>
          <div className="timeline-end">Debt Free!</div>
        </div>
        
        <div className="timeline-container">
          <div className="timeline-line"></div>
          
          {progressData.map((debt, index) => (
            <div 
              key={debt.id} 
              className="timeline-debt"
              style={{
                left: `${(debt.payoffMonth / totalMonths) * 90}%`,
                top: `${index * 60 + 20}px`
              }}
            >
              <div className="timeline-marker"></div>
              <div className="timeline-debt-info">
                <div className="debt-name">{debt.name}</div>
                <div className="debt-payoff">
                  Month {debt.payoffMonth}
                  {payoffSchedule[debt.payoffMonth - 1] && 
                    ` - ${formatDate(payoffSchedule[debt.payoffMonth - 1].date)}`
                  }
                </div>
                <div className="debt-balance">{formatCurrency(debt.currentBalance)}</div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="timeline-scale">
          {Array.from({length: Math.min(totalMonths, 12)}, (_, i) => (
            <div key={i} className="scale-mark">
              <div className="scale-number">{i * Math.ceil(totalMonths / 12)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Progress View Component
  const ProgressView = () => {
    const progressData = getProgressData();
    const totalDebt = getTotalDebt();
    
    return (
      <div className="progress-view">
        <div className="progress-summary">
          <div className="progress-stat">
            <div className="stat-label">Total Progress</div>
            <div className="stat-value">
              {totalDebt > 0 ? '0%' : '100%'} Complete
            </div>
          </div>
          <div className="progress-stat">
            <div className="stat-label">Time to Freedom</div>
            <div className="stat-value">
              {payoffSchedule.length} months
            </div>
          </div>
        </div>
        
        <div className="debt-progress-list">
          {progressData.map((debt, index) => (
            <div key={debt.id} className="debt-progress-item">
              <div className="debt-progress-header">
                <span className="debt-name">{debt.name}</span>
                <span className="debt-amount">{formatCurrency(debt.currentBalance)}</span>
              </div>
              
              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{
                      width: `${((payoffSchedule.length - debt.payoffMonth) / payoffSchedule.length) * 100}%`,
                      backgroundColor: index === 0 ? 'var(--primary-color)' : 'var(--gray-300)'
                    }}
                  ></div>
                </div>
                <div className="progress-label">
                  Payoff: Month {debt.payoffMonth}
                  {index === 0 && <span className="current-focus"> (Current Focus)</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Chart View Component
  const ChartView = () => {
    const chartData = getChartData();
    const maxDebt = Math.max(...chartData.map(d => d.totalDebt));
    
    return (
      <div className="chart-view">
        <div className="chart-header">
          <h4>Debt Reduction Over Time</h4>
        </div>
        
        <div className="chart-container">
          <div className="chart-y-axis">
            {Array.from({length: 5}, (_, i) => (
              <div key={i} className="y-axis-label">
                {formatCurrency(maxDebt * (1 - i * 0.25))}
              </div>
            ))}
          </div>
          
          <div className="chart-area">
            <svg viewBox="0 0 300 200" className="debt-chart">
              <defs>
                <linearGradient id="debtGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="var(--danger-color)" stopOpacity="0.3"/>
                  <stop offset="100%" stopColor="var(--danger-color)" stopOpacity="0.1"/>
                </linearGradient>
              </defs>
              
              {/* Chart lines */}
              <polyline
                points={chartData.map((d, i) => 
                  `${(i / (chartData.length - 1)) * 280 + 10},${190 - (d.totalDebt / maxDebt) * 180}`
                ).join(' ')}
                fill="none"
                stroke="var(--danger-color)"
                strokeWidth="3"
                strokeLinecap="round"
              />
              
              {/* Fill area */}
              <polygon
                points={[
                  '10,190',
                  ...chartData.map((d, i) => 
                    `${(i / (chartData.length - 1)) * 280 + 10},${190 - (d.totalDebt / maxDebt) * 180}`
                  ),
                  '290,190'
                ].join(' ')}
                fill="url(#debtGradient)"
              />
              
              {/* Data points */}
              {chartData.map((d, i) => (
                <circle
                  key={i}
                  cx={(i / (chartData.length - 1)) * 280 + 10}
                  cy={190 - (d.totalDebt / maxDebt) * 180}
                  r="4"
                  fill="var(--danger-color)"
                  stroke="var(--white)"
                  strokeWidth="2"
                />
              ))}
            </svg>
          </div>
          
          <div className="chart-x-axis">
            {chartData.filter((_, i) => i % 6 === 0).map((d, i) => (
              <div key={i} className="x-axis-label">
                {formatDate(d.date)}
              </div>
            ))}
          </div>
        </div>
        
        <div className="chart-legend">
          <div className="legend-item">
            <div className="legend-color" style={{backgroundColor: 'var(--danger-color)'}}></div>
            <span>Total Remaining Debt</span>
          </div>
        </div>
      </div>
    );
  };

  // Enhanced List View Component
  const ListView = () => {
    const monthsToShow = payoffSchedule.slice(0, 24);
    
    return (
      <div className="list-view">
        <div className="list-summary">
          <div className="summary-grid">
            <div className="summary-item">
              <div className="summary-label">Total Payments</div>
              <div className="summary-value">
                {formatCurrency(payoffSchedule.reduce((sum, month) => sum + month.totalPaid, 0))}
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-label">Interest Saved</div>
              <div className="summary-value">
                {formatCurrency(payoffSchedule.reduce((sum, month) => 
                  sum + month.debts.reduce((dSum, debt) => dSum + (debt.interestCharge || 0), 0), 0
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="enhanced-schedule-list">
          {monthsToShow.map((month) => (
            <div key={month.month} className="enhanced-schedule-month">
              <div className="month-header">
                <div className="month-info">
                  <span className="month-number">Month {month.month}</span>
                  <span className="month-date">{formatDate(month.date)}</span>
                </div>
                <div className="month-summary">
                  <span className="month-payment">{formatCurrency(month.totalPaid)}</span>
                  <span className="month-remaining">{formatCurrency(month.remainingBalance)} remaining</span>
                </div>
              </div>
              
              <div className="month-debts">
                {month.debts.map((debt) => (
                  <div key={debt.id} className="enhanced-schedule-debt">
                    <div className="debt-header">
                      <span className="debt-name">{debt.name}</span>
                      {debt.isPaidOff && <span className="paid-off-badge">PAID OFF!</span>}
                    </div>
                    
                    {debt.payment > 0 && (
                      <div className="debt-payment-details">
                        <div className="payment-row">
                          <span>Payment:</span>
                          <span>{formatCurrency(debt.payment)}</span>
                        </div>
                        <div className="payment-row">
                          <span>Interest:</span>
                          <span>{formatCurrency(debt.interestCharge || 0)}</span>
                        </div>
                        <div className="payment-row">
                          <span>Principal:</span>
                          <span>{formatCurrency(debt.principalPayment || 0)}</span>
                        </div>
                        <div className="payment-row total">
                          <span>Remaining:</span>
                          <span>{formatCurrency(debt.remainingBalance)}</span>
                        </div>
                      </div>
                    )}
                    
                    {debt.payment > 0 && (
                      <button
                        className={`mark-paid-button ${isPaymentPaid(debt.id, month.month) ? 'paid' : ''}`}
                        onClick={() => handleMarkPaymentPaid(debt.id, month.month, debt.payment)}
                        disabled={isPaymentPaid(debt.id, month.month)}
                      >
                        {isPaymentPaid(debt.id, month.month) ? '✓ Paid' : 'Mark Paid'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="debt-snowball">
      {/* Header */}
      <div className="debt-header">
        <button className="back-button" onClick={onClose}>
          ← Back
        </button>
        <h1>Debt Snowball</h1>
        <button 
          className="add-button"
          onClick={() => setShowAddDebtDialog(true)}
        >
          +
        </button>
      </div>

      {/* Summary Cards */}
      <div className="debt-summary">
        <div className="summary-card">
          <div className="summary-label">Total Debt</div>
          <div className="summary-value">{formatCurrency(getTotalDebt())}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Monthly Minimums</div>
          <div className="summary-value">{formatCurrency(getTotalMinimumPayments())}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Debt-Free Date</div>
          <div className="summary-value">
            {getDebtFreeDate() ? formatDate(getDebtFreeDate()) : 'N/A'}
          </div>
        </div>
      </div>

      {/* Extra Payment Input */}
      <div className="extra-payment-section">
        <h3>Extra Payment</h3>
        <div className="input-with-symbol">
          <span>$</span>
          <input
            type="number"
            value={extraPayment}
            onChange={(e) => setExtraPayment(e.target.value)}
            placeholder="Enter extra payment amount"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <button 
          className="import-button"
          onClick={() => setShowImportDialog(true)}
        >
          Import from Bills
        </button>
        <button 
          className="schedule-button"
          onClick={() => setShowScheduleDialog(true)}
          disabled={debts.length === 0}
        >
          📊 View Payoff Plan
        </button>
      </div>

      {/* Debt Cards */}
      <div className="debt-cards">
        {debts
          .sort((a, b) => a.currentBalance - b.currentBalance)
          .map((debt, index) => (
          <div 
            key={debt.id} 
            className={`debt-card ${isDebtPaidOff(debt.id) ? 'paid-off' : ''}`}
          >
            <div className="debt-card-header">
              <h3>{debt.name}</h3>
              <button 
                className="edit-button"
                onClick={() => {
                  setEditingDebt({...debt});
                  setShowEditDebtDialog(true);
                }}
              >
                ✏️
              </button>
            </div>
            
            <div className="debt-details">
              <div className="detail-row">
                <span>Balance:</span>
                <span className="balance">{formatCurrency(debt.currentBalance)}</span>
              </div>
              <div className="detail-row">
                <span>APR:</span>
                <span>{debt.apr}%</span>
              </div>
              <div className="detail-row">
                <span>Minimum:</span>
                <span>{formatCurrency(debt.minimumPayment)}</span>
              </div>
              <div className="detail-row">
                <span>Due Day:</span>
                <span>{debt.dueDay}</span>
              </div>
              {getDebtPayoffDate(debt.id) && (
                <div className="detail-row">
                  <span>Payoff Date:</span>
                  <span className="payoff-date">
                    {formatDate(getDebtPayoffDate(debt.id))}
                  </span>
                </div>
              )}
            </div>

            {isDebtPaidOff(debt.id) && (
              <div className="paid-off-overlay">
                <div className="shredded-effect"></div>
                <div className="paid-stamp">PAID OFF!</div>
              </div>
            )}

            <div className="debt-actions">
              <button 
                className="remove-button"
                onClick={() => handleRemoveDebt(debt.id)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      {debts.length === 0 && (
        <div className="empty-state">
          <h3>No Debts Added</h3>
          <p>Start by importing bills or adding debts manually</p>
        </div>
      )}

      {/* Import Bills Dialog */}
      {showImportDialog && (
        <div className="mobile-dialog">
          <div className="dialog-content">
            <h3>Import Bills as Debts</h3>
            <div className="bills-list">
              {bills.map(bill => (
                <label key={bill.id} className="bill-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedBills.includes(bill.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedBills([...selectedBills, bill.id]);
                      } else {
                        setSelectedBills(selectedBills.filter(id => id !== bill.id));
                      }
                    }}
                  />
                  <span>{bill.name} - {formatCurrency(bill.amount)}</span>
                </label>
              ))}
            </div>
            <div className="dialog-buttons">
              <button onClick={() => setShowImportDialog(false)}>Cancel</button>
              <button 
                onClick={handleImportBills}
                disabled={selectedBills.length === 0}
              >
                Import Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Debt Dialog */}
      {showAddDebtDialog && (
        <div className="mobile-dialog">
          <div className="dialog-content">
            <h3>Add New Debt</h3>
            <input
              type="text"
              placeholder="Debt Name"
              value={newDebt.name}
              onChange={(e) => setNewDebt({...newDebt, name: e.target.value})}
            />
            <input
              type="number"
              placeholder="Current Balance"
              value={newDebt.currentBalance}
              onChange={(e) => setNewDebt({...newDebt, currentBalance: e.target.value})}
              step="0.01"
            />
            <input
              type="number"
              placeholder="APR (%)"
              value={newDebt.apr}
              onChange={(e) => setNewDebt({...newDebt, apr: e.target.value})}
              step="0.01"
            />
            <input
              type="number"
              placeholder="Minimum Payment"
              value={newDebt.minimumPayment}
              onChange={(e) => setNewDebt({...newDebt, minimumPayment: e.target.value})}
              step="0.01"
            />
            <input
              type="number"
              placeholder="Due Day (1-31)"
              value={newDebt.dueDay}
              onChange={(e) => setNewDebt({...newDebt, dueDay: e.target.value})}
              min="1"
              max="31"
            />
            <select
              value={newDebt.frequency}
              onChange={(e) => setNewDebt({...newDebt, frequency: e.target.value})}
            >
              <option value="Monthly">Monthly</option>
              <option value="Weekly">Weekly</option>
              <option value="Biweekly">Biweekly</option>
            </select>
            <div className="dialog-buttons">
              <button onClick={() => setShowAddDebtDialog(false)}>Cancel</button>
              <button 
                onClick={handleAddDebt}
                disabled={!newDebt.name || !newDebt.currentBalance || !newDebt.minimumPayment}
              >
                Add Debt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Debt Dialog */}
      {showEditDebtDialog && editingDebt && (
        <div className="mobile-dialog">
          <div className="dialog-content">
            <h3>Edit Debt</h3>
            <input
              type="text"
              placeholder="Debt Name"
              value={editingDebt.name}
              onChange={(e) => setEditingDebt({...editingDebt, name: e.target.value})}
            />
            <input
              type="number"
              placeholder="Current Balance"
              value={editingDebt.currentBalance}
              onChange={(e) => setEditingDebt({...editingDebt, currentBalance: parseFloat(e.target.value) || 0})}
              step="0.01"
            />
            <input
              type="number"
              placeholder="APR (%)"
              value={editingDebt.apr}
              onChange={(e) => setEditingDebt({...editingDebt, apr: parseFloat(e.target.value) || 0})}
              step="0.01"
            />
            <input
              type="number"
              placeholder="Minimum Payment"
              value={editingDebt.minimumPayment}
              onChange={(e) => setEditingDebt({...editingDebt, minimumPayment: parseFloat(e.target.value) || 0})}
              step="0.01"
            />
            <input
              type="number"
              placeholder="Due Day (1-31)"
              value={editingDebt.dueDay}
              onChange={(e) => setEditingDebt({...editingDebt, dueDay: parseInt(e.target.value) || 1})}
              min="1"
              max="31"
            />
            <select
              value={editingDebt.frequency}
              onChange={(e) => setEditingDebt({...editingDebt, frequency: e.target.value})}
            >
              <option value="Monthly">Monthly</option>
              <option value="Weekly">Weekly</option>
              <option value="Biweekly">Biweekly</option>
            </select>
            <div className="dialog-buttons">
              <button onClick={() => setShowEditDebtDialog(false)}>Cancel</button>
              <button onClick={handleEditDebt}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Schedule Dialog */}
      {showScheduleDialog && (
        <div className="mobile-dialog schedule-dialog">
          <div className="dialog-content">
            <div className="schedule-header">
              <h3>Payoff Schedule</h3>
              <button onClick={() => setShowScheduleDialog(false)}>&times;</button>
            </div>
            
            {/* View Mode Selector */}
            <div className="view-mode-selector">
              <button 
                className={`view-mode-btn ${scheduleViewMode === 'timeline' ? 'active' : ''}`}
                onClick={() => setScheduleViewMode('timeline')}
              >
                📅 Timeline
              </button>
              <button 
                className={`view-mode-btn ${scheduleViewMode === 'progress' ? 'active' : ''}`}
                onClick={() => setScheduleViewMode('progress')}
              >
                📊 Progress
              </button>
              <button 
                className={`view-mode-btn ${scheduleViewMode === 'chart' ? 'active' : ''}`}
                onClick={() => setScheduleViewMode('chart')}
              >
                📈 Chart
              </button>
              <button 
                className={`view-mode-btn ${scheduleViewMode === 'list' ? 'active' : ''}`}
                onClick={() => setScheduleViewMode('list')}
              >
                📋 Details
              </button>
            </div>
            
            {/* Render Selected View */}
            <div className="schedule-content">
              {scheduleViewMode === 'timeline' && <TimelineView />}
              {scheduleViewMode === 'progress' && <ProgressView />}
              {scheduleViewMode === 'chart' && <ChartView />}
              {scheduleViewMode === 'list' && <ListView />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebtSnowball; 